/**
 * Feedback Utilities
 * 
 * Provides functions for managing user feedback submission and statistics.
 * These are pure functions that can be easily tested.
 * 
 * @module feedbackUtils
 */

import { storage, STORAGE_KEYS } from './storage';
import { RATING } from '../constants/gameplay';
import { TIMEOUTS } from '../constants/timing';

/**
 * Create a new feedback entry
 * 
 * @param {Object} params - Feedback parameters
 * @param {string} params.category - Feedback category
 * @param {string} params.message - Feedback message
 * @param {number} params.rating - Feedback rating (1-5)
 * @param {string} params.currentScreen - Current screen where feedback was submitted
 * @param {Object} params.progress - User progress snapshot
 * 
 * @returns {Object} New feedback object with id, timestamp, and metadata
 * 
 * @example
 * const feedback = createFeedbackEntry({
 *   category: 'bug',
 *   message: 'Found a bug',
 *   rating: 3,
 *   currentScreen: 'game',
 *   progress: { totalScore: 100 }
 * });
 */
export const createFeedbackEntry = ({ category, message, rating, currentScreen, progress }) => {
  return {
    id: Date.now(),
    category,
    message: message.trim(),
    rating,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    currentScreen,
    progress: {
      totalScore: progress?.totalScore || 0,
      completedSubjects: progress?.completedSubjects ? Object.keys(progress.completedSubjects).length : 0,
      learningStreak: progress?.learningStreak || 0
    }
  };
};

/**
 * Calculate feedback statistics from a feedback array
 * 
 * @param {Array<Object>} feedback - Array of feedback objects
 * 
 * @returns {Object} Statistics object with:
 *   - total: Total number of feedback entries
 *   - avgRating: Average rating (0-5)
 *   - categories: Object mapping category names to counts
 * 
 * @example
 * const stats = calculateFeedbackStats([
 *   { category: 'bug', rating: 3 },
 *   { category: 'feature', rating: 5 }
 * ]);
 * // { total: 2, avgRating: 4, categories: { bug: 1, feature: 1 } }
 */
export const calculateFeedbackStats = (feedback = []) => {
  const total = feedback.length;
  const avgRating = total > 0 
    ? feedback.reduce((sum, f) => sum + (f.rating || 0), 0) / total 
    : 0;
  
  const categories = feedback.reduce((acc, f) => {
    const category = f.category || 'general';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});
  
  return { total, avgRating, categories };
};

/**
 * Submit feedback and update storage
 * 
 * Note: This function handles the data transformation and storage.
 * UI interactions (sounds, haptics, messages) should be handled by the caller.
 * 
 * @param {Object} params - Submission parameters
 * @param {string} params.message - Feedback message
 * @param {string} params.category - Feedback category
 * @param {number} params.rating - Feedback rating
 * @param {string} params.currentScreen - Current screen
 * @param {Object} params.progress - User progress
 * @param {Array<Object>} params.existingFeedback - Current feedback array
 * 
 * @returns {Object} Result object with:
 *   - success: Boolean indicating success
 *   - updatedFeedback: New feedback array with the entry added
 *   - newEntry: The newly created feedback entry
 * 
 * @example
 * const result = submitFeedback({
 *   message: 'Great app!',
 *   category: 'general',
 *   rating: 5,
 *   currentScreen: 'home',
 *   progress: { totalScore: 100 },
 *   existingFeedback: []
 * });
 */
export const submitFeedback = ({
  message,
  category,
  rating,
  currentScreen,
  progress,
  existingFeedback = []
}) => {
  // Validate input
  if (!message || !message.trim()) {
    return {
      success: false,
      error: 'Feedback message is required',
      updatedFeedback: existingFeedback,
      newEntry: null
    };
  }
  
  // Create new feedback entry
  const newEntry = createFeedbackEntry({
    category: category || 'general',
    message,
    rating: rating || RATING.DEFAULT,
    currentScreen,
    progress
  });
  
  // Update feedback array
  const updatedFeedback = [...existingFeedback, newEntry];
  
  // Save to storage
  storage.set(STORAGE_KEYS.FEEDBACK, updatedFeedback);
  
  return {
    success: true,
    updatedFeedback,
    newEntry
  };
};

/**
 * Get default feedback form values
 * 
 * @returns {Object} Default values for feedback form
 */
export const getDefaultFeedbackValues = () => ({
  message: '',
  rating: RATING.DEFAULT,
  category: 'general'
});

/**
 * Get success message for feedback submission
 * 
 * @returns {string} Success message text
 */
export const getFeedbackSuccessMessage = () => {
  return 'Thank you for your feedback! We appreciate your input.';
};

